import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Layers,
  Search,
  IndianRupee,
  Building2,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { categoriesAPI, stationsAPI } from '../../services/api';

const TIME_SLOTS = ['1hr', '3hr', '6hr', '9hr', '12hr', '24hr'];
const SLOT_LABELS = {
  '1hr': '1 Hour',
  '3hr': '3 Hours',
  '6hr': '6 Hours',
  '9hr': '9 Hours',
  '12hr': '12 Hours',
  '24hr': '24 Hours'
};

const CategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStation, setFilterStation] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    station: '',
    pricing: {
      '1hr': '',
      '3hr': '',
      '6hr': '',
      '9hr': '',
      '12hr': '',
      '24hr': ''
    },
    description: '',
    amenities: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesRes, stationsRes] = await Promise.all([
        categoriesAPI.getAll(),
        stationsAPI.getAll()
      ]);
      setCategories(categoriesRes.data.categories);
      setStations(stationsRes.data.stations);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        station: category.station?._id || '',
        pricing: {
          '1hr': category.pricing?.['1hr']?.toString() || '',
          '3hr': category.pricing?.['3hr']?.toString() || '',
          '6hr': category.pricing?.['6hr']?.toString() || '',
          '9hr': category.pricing?.['9hr']?.toString() || '',
          '12hr': category.pricing?.['12hr']?.toString() || '',
          '24hr': category.pricing?.['24hr']?.toString() || category.basePrice?.toString() || ''
        },
        description: category.description || '',
        amenities: category.amenities?.join(', ') || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        station: '',
        pricing: {
          '1hr': '',
          '3hr': '',
          '6hr': '',
          '9hr': '',
          '12hr': '',
          '24hr': ''
        },
        description: '',
        amenities: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePricingChange = (slot, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [slot]: value
      }
    }));
  };

  const handleAutoCalculate = () => {
    const basePrice = parseFloat(formData.pricing['24hr']);
    if (!basePrice || basePrice <= 0) {
      toast.error('Please enter the 24-hour price first');
      return;
    }
    
    // Calculate proportional pricing
    setFormData(prev => ({
      ...prev,
      pricing: {
        '1hr': Math.round(basePrice * 0.1).toString(),
        '3hr': Math.round(basePrice * 0.2).toString(),
        '6hr': Math.round(basePrice * 0.35).toString(),
        '9hr': Math.round(basePrice * 0.45).toString(),
        '12hr': Math.round(basePrice * 0.6).toString(),
        '24hr': basePrice.toString()
      }
    }));
    toast.success('Prices auto-calculated');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all pricing fields
    for (const slot of TIME_SLOTS) {
      if (!formData.pricing[slot] || parseFloat(formData.pricing[slot]) <= 0) {
        toast.error(`Please enter a valid price for ${SLOT_LABELS[slot]}`);
        return;
      }
    }
    
    const data = {
      name: formData.name,
      station: formData.station,
      pricing: {
        '1hr': parseFloat(formData.pricing['1hr']),
        '3hr': parseFloat(formData.pricing['3hr']),
        '6hr': parseFloat(formData.pricing['6hr']),
        '9hr': parseFloat(formData.pricing['9hr']),
        '12hr': parseFloat(formData.pricing['12hr']),
        '24hr': parseFloat(formData.pricing['24hr'])
      },
      description: formData.description,
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a)
    };
    
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory._id, data);
        toast.success('Category updated successfully');
      } else {
        await categoriesAPI.create(data);
        toast.success('Category created successfully');
      }
      
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.error || 'Failed to save category');
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      return;
    }
    
    try {
      await categoriesAPI.delete(category._id);
      toast.success('Category deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.error || 'Failed to delete category');
    }
  };

  const filteredCategories = categories.filter((category) => {
    const matchesSearch = 
      category.name.toLowerCase().includes(search.toLowerCase()) ||
      category.station?.name?.toLowerCase().includes(search.toLowerCase());
    const matchesStation = !filterStation || category.station?._id === filterStation;
    return matchesSearch && matchesStation;
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Room Categories
            </h1>
            <p className="text-midnight-600 mt-1">
              Manage room categories and time-slot pricing
            </p>
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Category
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
            <input
              type="text"
              placeholder="Search categories..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-12"
            />
          </div>
          <select
            value={filterStation}
            onChange={(e) => setFilterStation(e.target.value)}
            className="input-field max-w-xs"
          >
            <option value="">All Stations</option>
            {stations.map((station) => (
              <option key={station._id} value={station._id}>
                {station.name} ({station.code})
              </option>
            ))}
          </select>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="card text-center py-20">
            <Layers className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-midnight-700">No categories found</h3>
            <p className="text-midnight-500 mt-2">Add a new category to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCategories.map((category) => (
              <div key={category._id} className="card">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-display font-semibold text-midnight-900">
                        {category.name}
                      </h3>
                      <p className="text-sm text-midnight-500 flex items-center gap-1 mt-1">
                        <Building2 className="w-4 h-4" />
                        {category.station?.name} ({category.station?.code})
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenModal(category)}
                        className="p-2 text-midnight-500 hover:text-railway-500 hover:bg-railway-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="p-2 text-midnight-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {category.description && (
                    <p className="text-midnight-600 text-sm mb-4">
                      {category.description}
                    </p>
                  )}
                  
                  {category.amenities && category.amenities.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {category.amenities.map((amenity, index) => (
                        <span 
                          key={index}
                          className="px-2 py-1 bg-midnight-100 rounded-md text-xs text-midnight-600"
                        >
                          {amenity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Expandable Pricing Table */}
                <div className="border-t border-midnight-100">
                  <button
                    onClick={() => setExpandedCategory(
                      expandedCategory === category._id ? null : category._id
                    )}
                    className="w-full px-6 py-3 flex items-center justify-between bg-midnight-50 hover:bg-midnight-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-railway-500" />
                      <span className="text-sm font-medium text-midnight-700">Time Slot Pricing</span>
                    </div>
                    {expandedCategory === category._id ? (
                      <ChevronUp className="w-4 h-4 text-midnight-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-midnight-500" />
                    )}
                  </button>
                  
                  {expandedCategory === category._id && (
                    <div className="px-6 py-4 bg-white">
                      <div className="grid grid-cols-6 gap-2">
                        {TIME_SLOTS.map(slot => (
                          <div key={slot} className="text-center p-2 bg-midnight-50 rounded-lg">
                            <div className="text-xs text-midnight-500 mb-1">{slot}</div>
                            <div className="flex items-center justify-center gap-0.5 font-bold text-midnight-900">
                              <IndianRupee className="w-3 h-3" />
                              <span>{category.pricing?.[slot] || '—'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="px-6 py-4 bg-gradient-to-r from-railway-600 to-railway-500 text-white">
                  <div className="flex items-center justify-between">
                    <span className="text-white/80 text-sm">Starting from</span>
                    <div className="flex items-center gap-1 text-xl font-bold">
                      <IndianRupee className="w-5 h-5" />
                      <span>{category.pricing?.['1hr'] || category.basePrice}</span>
                      <span className="text-sm font-normal text-white/70">/hr</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Category Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., AC Deluxe"
                className="input-field"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Station *
              </label>
              <select
                name="station"
                value={formData.station}
                onChange={handleChange}
                className="input-field"
                required
                disabled={!!editingCategory}
              >
                <option value="">Select a station</option>
                {stations.map((station) => (
                  <option key={station._id} value={station._id}>
                    {station.name} ({station.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Time Slot Pricing */}
          <div className="p-4 bg-railway-50 rounded-xl border border-railway-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-railway-900 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Time Slot Pricing *
              </h3>
              <button
                type="button"
                onClick={handleAutoCalculate}
                className="text-sm text-railway-600 hover:text-railway-800 underline"
              >
                Auto-calculate from 24hr price
              </button>
            </div>
            
            <div className="grid grid-cols-6 gap-3">
              {TIME_SLOTS.map(slot => (
                <div key={slot}>
                  <label className="block text-xs font-medium text-railway-700 mb-1">
                    {SLOT_LABELS[slot]}
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-railway-400" />
                    <input
                      type="number"
                      value={formData.pricing[slot]}
                      onChange={(e) => handlePricingChange(slot, e.target.value)}
                      placeholder="0"
                      className="w-full px-2 pl-6 py-2 text-sm border border-railway-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-railway-500 focus:border-transparent"
                      min="0"
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-railway-600 mt-3">
              💡 Tip: Enter the 24-hour price and click "Auto-calculate" to generate proportional prices for other slots.
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="e.g., Air-conditioned deluxe room with attached bathroom"
              className="input-field"
              rows={3}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Amenities (comma-separated)
            </label>
            <input
              type="text"
              name="amenities"
              value={formData.amenities}
              onChange={handleChange}
              placeholder="e.g., TV, WiFi, Hot Water, Room Service"
              className="input-field"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={handleCloseModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              {editingCategory ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default CategoriesManagement;
